/**
 * src/stores/auth.ts
 * --------------------
 * Zustand store for customer authentication state.
 *
 * Fully implemented this will:
 *   - Hold user, accessToken, isAuthenticated, isLoading.
 *   - setUser(user, token) — stores credentials in memory (token) and
 *     persists user in sessionStorage (not localStorage for security).
 *   - clearAuth() — clears all auth state on sign-out.
 *   - The access token is stored ONLY in memory (this store), never in
 *     localStorage, to minimize XSS exposure. The refresh token is in
 *     an httpOnly cookie managed by the backend.
 *
 * Key dependencies:
 *   zustand, src/types/auth.ts
 */
import { create } from 'zustand'
import type { CustomerUser } from '@/types/auth'

interface AuthStore {
  user: CustomerUser | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean

  setUser: (user: CustomerUser, token: string) => void
  clearAuth: () => void
  setLoading: (v: boolean) => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,

  setUser: (user, token) => {
    // Persist user profile in sessionStorage for page reload restore
    sessionStorage.setItem('helix_user', JSON.stringify(user))
    set({ user, accessToken: token, isAuthenticated: true, isLoading: false })
  },

  clearAuth: () => {
    sessionStorage.removeItem('helix_user')
    set({ user: null, accessToken: null, isAuthenticated: false })
  },

  setLoading: (v) => set({ isLoading: v }),
}))
